import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import PayoutDetails from 'components/modules/accounting/payouts/payout-details';

import Badge from 'components/v2/Badge';
import { humanize } from 'underscore.string';
import Tooltip from 'components/v2/Tooltip';
import CargomaticModal from 'components/v2/modal';
import ApproveButton from 'components/v2/buttons/ApproveButton';
import { getPayoutsForShipment } from 'utils/AccountingWebAPIUtils';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import SessionStore from 'stores/SessionStore';
import { useTranslation } from 'react-i18next';
import formatMoney from 'utils/formatters/Money';
import ExternalLink from 'components/v2/ExternalLink';
import { sluggify } from 'utils/localization/utils';
import AdjustmentTitle from './AdjustmentTitle';
import AdjustmentAction from './AdjustmentAction';
import DateField from './DateField';
import AddBaseCostForm from './AddBaseCostForm';

export default function CarrierCharges({
  shipmentId,
  shipmentIsCurrent,
  charges,
  timezone,
  total,
  currency,
  isIntermodalShipmentType,
  onAddCustomAdjustment,
  onAddInternalAdjustment,
  onAddCustomerAdjustment,
  onAddBaseCost,
  onApproveBaseCost,
  onRemoveLineItem,
}) {
  const { t } = useTranslation();

  const [lineItems, setLineItems] = useState([]);
  const [selectedLineItem, setSelectedLineItem] = useState();
  const [selectedBaseCost, setSelectedBaseCost] = useState();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfirmApprovalModal, setShowConfirmApprovalModal] =
    useState(false);
  const [showConfirmRemovalModal, setShowConfirmRemovalModal] = useState(false);

  function handleHideDetailsModal() {
    setShowDetailsModal(false);
  }

  function handleApprove(lineItem) {
    return function () {
      setShowConfirmApprovalModal(true);
      setSelectedBaseCost(lineItem);
    };
  }

  function handleRemove(lineItem) {
    return function () {
      setShowConfirmRemovalModal(true);
      setSelectedLineItem(lineItem);
    };
  }

  function handleHideConfirmApprovalModal() {
    setShowConfirmApprovalModal(false);
    setSelectedLineItem(null);
    getPayouts();
  }

  function handleConfirmApproveBaseCost() {
    onApproveBaseCost(selectedBaseCost);
    handleHideConfirmApprovalModal();
  }

  async function handleConfirmLineItemRemoval() {
    await onRemoveLineItem(selectedLineItem, 'payables');
    handleHideConfirmRemovalModal();
  }

  function handleHideConfirmRemovalModal() {
    setShowConfirmRemovalModal(false);
    setSelectedLineItem(null);
  }

  async function getPayouts() {
    try {
      const {
        entity: { payouts },
      } = await getPayoutsForShipment(shipmentId);

      const mappedLineItems = charges.map((lineItem) => {
        const isFirstFuelSurchargeItem = charges.find(c => c.charge_code === 'FSC')?._id === lineItem._id
        const payout = payouts.find((p) => p._id === lineItem.payout_id);
        if (!payout) {
          return {
            ...lineItem,
            payout: null,
            isFirstFuelSurchargeItem
          };
        }
        const { _id, number, timestamp, status, paid_date } = payout;
        return {
          ...lineItem,
          payout: {
            _id,
            number,
            timestamp,
            status,
            paid_date,
          },
          isFirstFuelSurchargeItem
        };
      });

      /* 
       * This section of code sorts the line item charges for easy 
       * identification of quick pay line item, starting with the parent line 
       * item, followed by relevant quick pay discounts, and so on. 
       * This improves readabilty for the admin user 
       */

      const parentCharges = mappedLineItems.filter(item => !item.parent_adjustment_id);
      const childrenCharges = mappedLineItems.filter(item => item.parent_adjustment_id);

      const childrenMap = childrenCharges.reduce((map, child) => {
        const parentId = child.parent_adjustment_id;
        if (!map[parentId]) {
          map[parentId] = [];
        }
        map[parentId].push(child);
        return map;
      }, {});

      const arrangeLineItems = [];
      parentCharges.forEach(parent => {
        arrangeLineItems.push(parent);

        // Check for children with `_id` as parent reference.
        if (childrenMap[parent._id]) {
          arrangeLineItems.push(...childrenMap[parent._id]);
        }
        
        // Check for children with `accessorial_request_id` as parent reference.
        if (parent.accessorial_request_id && childrenMap[parent.accessorial_request_id]) {
          arrangeLineItems.push(...childrenMap[parent.accessorial_request_id]);
        }
      });

      setLineItems(arrangeLineItems);
    } catch (e) {
      console.log('Error getting payouts', e);
    }
  }

  function renderPayoutLink(lineItem) {
    if (!lineItem.payout) {
      return null;
    }
    const {
      payout: { _id, number },
    } = lineItem;

    return (
      <ExternalLink
        dataTestId="ExternalLink-8f6da654"
        href={`/payouts/${_id}`}
        title={number}
        showIcon />
    );
  }

  function showApproveButton(lineItem) {
    const { type, category, parent_adjustment_id } = lineItem;

    return (
      isIntermodalShipmentType &&
      (type === 'carrier_cost' || category === 'base_cost') && !parent_adjustment_id
    );
  }

  function showRemoveButton({ processed, accessorial_request_id }) {
    return (
      (SessionStore.isFinance() || SessionStore.isFinanceAdmin()) &&
      ((!processed && isIntermodalShipmentType && !accessorial_request_id) ||
        (!isIntermodalShipmentType && !processed))
    );
  }

  useEffect(() => {
    getPayouts();
  }, [charges]);

  function renderApproveButton(lineItem) {
    return (
      (<ApproveButton
        dataTestId="ApproveButton-1109d6b8"
        disabled={lineItem.approved}
        onClick={handleApprove(lineItem)} />)
    );
  }

  function renderRemoveButton(lineItem) {
    return (
      (<DeleteButton
        dataTestId="DeleteButton-aff10c6a"
        disabled={lineItem.processed}
        onClick={handleRemove(lineItem)} />)
    );
  }

  // Bens recommendation for accessorials beyond the 2 week period.
  // Some might be incorrectly marked but it was a trade off decision
  function getRateSource(lineItem) {
    if (!lineItem?.rate_source) {
      return lineItem?.autorated ? t('AUTOMATED') : t('MANUAL');
    }

    return t(sluggify(lineItem.rate_source));
  }

  function getColor(status) {
    const statusColorMap = {
      paid: 'green',
      approved_internally: 'blue',
      default: 'gray', // Default color
    };
    return statusColorMap[status] || statusColorMap.default;
  }

  function setChargeStatus(lineItem) {
    if (lineItem?.payout) {
      return (
        (<Badge dataTestId="Badge-df95852b" color={getColor(lineItem.payout?.status)}>
          {humanize(lineItem.payout?.status)}
        </Badge>)
      );
    }

    if (lineItem?.has_pending_state) {
      return <Badge dataTestId="Badge-d114a320" color="gray">{humanize(t('PENDING'))}</Badge>;
    }

    return null;
  }

  return (
    (<div data-testid="div-d35e450b">
      <div data-testid="div-a81af52c" className="">
        <h3 data-testid="h3-f3726b77" className="my-0 mb-4 text-base">{t('CARRIER_CHARGES')}</h3>
        <div data-testid="div-763fa60e" className="overflow-x-auto table-wrapper">
          <table data-testid="table-3bab90ce" className="default-table table-fixed">
            <thead data-testid="thead-24d618d2">
              <tr data-testid="tr-d37e1215">
                <th data-testid="th-b8048929" className="w-48">{t('CHARGE')}</th>
                <th data-testid="th-f0ade268" className="w-20">{t('COST')}</th>
                <th data-testid="th-314678fe" className="w-40">{t('ENTERED_DATE')}</th>
                <th data-testid="th-21a3223c" className="w-40">{t('RATE_SOURCE')}</th>
                <th data-testid="th-b02300c0" className="w-52">{t('APPROVED_PAYOUT')}</th>
                <th data-testid="th-aca19183" className="w-40">{t('APPROVED_PAYOUT_DATE')}</th>
                <th data-testid="th-2f1e7db9" className="w-24">{t('PAYOUT_STATUS')}</th>
                <th data-testid="th-b73bb107" className="w-32">{t('PAID_DATE')}</th>
                <th data-testid="th-b2590f55">&nbsp;</th>
              </tr>
            </thead>
            <tbody data-testid="tbody-c5ceb454">
              {lineItems.map((lineItem, index) => (<tr data-testid={`tr-75a4ad3d-${index}`} key={lineItem._id}>
                <td data-testid={`td-fcb8f8ec-${index}`} className="break-words">
                  <AdjustmentTitle
                    lineItem={lineItem}
                    unapplied={
                      isIntermodalShipmentType && !lineItem.equipment
                    }
                  />
                </td>
                <td data-testid={`td-042de8b8-${index}`}>
                  {formatMoney(
                    lineItem.value?.toFixed(2),
                    lineItem?.currency,
                  )}
                </td>
                <td data-testid={`td-2b5a7029-${index}`}>
                  <DateField date={lineItem.timestamp} timezone={timezone} />
                </td>
                <td data-testid={`td-d4fdca1f-${index}`} className="text-capitalize">{getRateSource(lineItem)}</td>
                <td data-testid={`td-50403cb8-${index}`}>{renderPayoutLink(lineItem)}</td>
                <td data-testid={`td-ed299596-${index}`}>
                  <DateField
                    date={lineItem.payout?.timestamp}
                    timezone={timezone}
                  />
                </td>
                <td data-testid={`td-da5052a8-${index}`}>
                  {setChargeStatus(lineItem)}
                </td>
                <td data-testid={`td-89c7346e-${index}`}>
                  <DateField
                    date={lineItem.payout?.paid_date}
                    timezone={timezone}
                    showTime={false}
                  />
                </td>
                <td data-testid={`td-4a199dc5-${index}`}>
                  <div
                    data-testid={`div-3f42374e-${index}`}
                    className="flex items-center space-x-2">
                    <AdjustmentAction
                      lineItem={lineItem}
                      shipmentIsCurrent={shipmentIsCurrent}
                      isIntermodalShipmentType={isIntermodalShipmentType}
                      onAddCustomAdjustment={onAddCustomAdjustment}
                      onAddCustomerAdjustment={onAddCustomerAdjustment}
                      onAddInternalAdjustment={onAddInternalAdjustment}
                    />
                    {showApproveButton(lineItem) && (
                      <Tooltip
                        dataTestId={`Tooltip-18d40ba7-${index}`}
                        id="approve-base-cost"
                        position="top"
                        body={() => renderApproveButton(lineItem)}>
                        {t('APPROVE_CARRIER_COST')}
                      </Tooltip>
                    )}
                    {showRemoveButton(lineItem) && (
                      <Tooltip
                        dataTestId={`Tooltip-e7f9684c-${index}`}
                        id="remove-line-item"
                        position="top"
                        body={() => renderRemoveButton(lineItem)}>
                        {t('REMOVE_LINE_ITEM')}
                      </Tooltip>
                    )}
                  </div>
                </td>
              </tr>))}
              <tr data-testid="tr-2145a9b9">
                <td data-testid="td-dec834fa">
                  <div data-testid="div-0cffa7dc" className="w-48">{t('TOTAL')}</div>
                </td>
                <td data-testid="td-72d7df69" className="font-bold">
                  {formatMoney(total, lineItems[0]?.currency)}
                </td>
                <td data-testid="td-07c4d5f1" colSpan={6}>&nbsp;</td>
              </tr>
            </tbody>
          </table>
          <AddBaseCostForm
            ledgerType="payables"
            ledgers={charges}
            onAddBaseCost={onAddBaseCost}
            currency={currency}
          />
        </div>
      </div>
      {showDetailsModal && (
        <CargomaticModal
          dataTestId="CargomaticModal-8ae21013"
          title=""
          show={showDetailsModal}
          footer={null}
          body={<PayoutDetails payoutId={selectedLineItem} />}
          onHide={handleHideDetailsModal}
          dialogClassName="modal-override"
          location="top" />
      )}
      {showConfirmApprovalModal && (
        <ConfirmModal
          dataTestId="ConfirmModal-1e376964"
          show={showConfirmApprovalModal}
          onConfirm={handleConfirmApproveBaseCost}
          onHide={handleHideConfirmApprovalModal}
          title={t('APPROVE_CARRIER_COST_TITLE')}
          message={t('APPROVE_CARRIER_COST_MESSAGE')}
          affirmative={t('APPROVE')}
          hideButtonText={t('NO')} />
      )}
      {showConfirmRemovalModal && (
        <ConfirmModal
          dataTestId="ConfirmModal-2fbe9ab9"
          show={showConfirmRemovalModal}
          onConfirm={handleConfirmLineItemRemoval}
          onHide={handleHideConfirmRemovalModal}
          title={t('APPROVE_ITEM_REMOVAL_TITLE')}
          message={t('APPROVE_ITEM_REMOVAL_MESSAGE')}
          affirmative={t('APPROVE')}
          hideButtonText={t('NO')} />
      )}
    </div>)
  );
}

CarrierCharges.propTypes = {
  timezone: PropTypes.string,
};

CarrierCharges.defaultProps = {
  timezone: 'America/Los_Angeles',
};
